import { useEffect, useState } from "react";
import { authApi, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { Loader2, Plus, Save, Users, ArrowUp, ArrowDown } from "lucide-react";

const inputCls =
  "bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 h-9 focus-visible:ring-0 focus-visible:border-[#0E6B3A]";

function SlotRow({ slot, client, reload }) {
  const [start, setStart] = useState(slot.start);
  const [end, setEnd] = useState(slot.end);
  const [cap, setCap] = useState(slot.capacity);
  const [saving, setSaving] = useState(false);
  const dirty = start !== slot.start || end !== slot.end || Number(cap) !== slot.capacity;

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/admin/slots/${slot.id}`, {
        start,
        end,
        capacity: Number(cap),
      });
      toast.success("Time slot updated.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`admin-slot-${slot.id}`}
      className="flex items-center gap-2 md:gap-3 py-2"
    >
      <Input value={start} onChange={(e) => setStart(e.target.value)} className={`${inputCls} w-24`} placeholder="8:00am" />
      <span className="text-[#6D726A]">–</span>
      <Input value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputCls} w-24`} placeholder="10:00am" />
      <div className="flex items-center gap-1.5 ml-2 text-sm text-[#6D726A]">
        <Users className="w-4 h-4" strokeWidth={1.6} />
        <Input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} className={`${inputCls} w-16`} />
      </div>
      <span className="text-xs text-[#6D726A] ml-1 hidden md:inline">{slot.taken} signed up</span>
      <div className="ml-auto flex items-center gap-1">
        {dirty && (
          <button
            data-testid={`admin-slot-save-${slot.id}`}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs text-[#0E6B3A] hover:text-[#083D21] px-2 py-1"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        )}
        <ConfirmDelete
          small
          testId={`admin-slot-delete-${slot.id}`}
          label="Delete this time slot"
          description={`Deletes this ${slot.start}–${slot.end} slot and removes any ${slot.taken} sign-up(s) on it.`}
          onConfirm={async () => {
            try {
              await client.delete(`/admin/slots/${slot.id}`);
              toast.success("Time slot deleted.");
              reload();
            } catch (e) {
              toast.error("Could not delete slot.");
            }
          }}
        />
      </div>
    </div>
  );
}

function AddSlot({ roleId, client, reload }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [cap, setCap] = useState(1);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!start || !end) {
      toast.error("Enter a start and end time.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/admin/slots", { role_id: roleId, start, end, capacity: Number(cap) });
      setStart("");
      setEnd("");
      setCap(1);
      toast.success("Time slot added.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 md:gap-3 py-2 mt-1">
      <Input value={start} onChange={(e) => setStart(e.target.value)} className={`${inputCls} w-24`} placeholder="start" data-testid={`admin-newslot-start-${roleId}`} />
      <span className="text-[#6D726A]">–</span>
      <Input value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputCls} w-24`} placeholder="end" data-testid={`admin-newslot-end-${roleId}`} />
      <div className="flex items-center gap-1.5 ml-2 text-sm text-[#6D726A]">
        <Users className="w-4 h-4" strokeWidth={1.6} />
        <Input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} className={`${inputCls} w-16`} />
      </div>
      <button
        data-testid={`admin-newslot-add-${roleId}`}
        onClick={add}
        disabled={busy}
        className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#8E1226] hover:text-[#0E6B3A] px-2 py-1"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        Add time
      </button>
    </div>
  );
}

function RoleBlock({ role, client, reload }) {
  const [name, setName] = useState(role.role);
  const [desc, setDesc] = useState(role.description || "");
  const [saving, setSaving] = useState(false);
  const dirty = name !== role.role || desc !== (role.description || "");

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/admin/roles/${role.id}`, { role: name, description: desc });
      toast.success("Role updated.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`admin-role-${role.id}`}
      className="rounded-xl border border-[#8E1226]/12 p-5 bg-[#FBF7F0]"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 font-serif text-xl focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
            data-testid={`admin-role-name-${role.id}`}
          />
          <Input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Short description"
            className="bg-transparent border-0 border-b border-[#8E1226]/15 rounded-none px-0 text-sm text-[#6D726A] focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
          />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {dirty && (
            <button
              data-testid={`admin-role-save-${role.id}`}
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-xs text-[#0E6B3A] px-2 py-1"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          )}
          <ConfirmDelete
            small
            testId={`admin-role-delete-${role.id}`}
            label="Delete this role"
            description="Deletes this role, all its time slots and any sign-ups on them."
            onConfirm={async () => {
              try {
                await client.delete(`/admin/roles/${role.id}`);
                toast.success("Role deleted.");
                reload();
              } catch (e) {
                toast.error("Could not delete role.");
              }
            }}
          />
        </div>
      </div>
      <div className="mt-4 divide-y divide-[#8E1226]/8">
        {role.slots.map((s) => (
          <SlotRow key={s.id} slot={s} client={client} reload={reload} />
        ))}
      </div>
      <AddSlot roleId={role.id} client={client} reload={reload} />
    </div>
  );
}

function AddRole({ dayId, client, reload }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await client.post("/admin/roles", { day_id: dayId, role: name.trim(), description: "" });
      setName("");
      toast.success("Role added.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 mt-4">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="New role name (e.g. Kitchen Porter)"
        className="bg-[#FBF7F0] border border-[#8E1226]/15 rounded-lg focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
        data-testid={`admin-newrole-name-${dayId}`}
      />
      <button
        data-testid={`admin-newrole-add-${dayId}`}
        onClick={add}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-[#8E1226] text-[#FBF7F0] px-5 py-2.5 text-sm hover:bg-[#0E6B3A] transition-colors shrink-0"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Add role
      </button>
    </div>
  );
}

function DayBlock({ day, client, reload, onMove, isFirst, isLast }) {
  const [label, setLabel] = useState(day.label);
  const [date, setDate] = useState(day.date);
  const [blurb, setBlurb] = useState(day.blurb || "");
  const [saving, setSaving] = useState(false);
  const dirty = label !== day.label || date !== day.date || blurb !== (day.blurb || "");

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/admin/days/${day.id}`, { label, date, blurb });
      toast.success("Day updated.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`admin-day-${day.id}`} className="rounded-2xl border border-[#8E1226]/12 p-6 md:p-8 mb-8">
      <div className="flex flex-wrap items-end gap-4 pb-6 border-b border-[#8E1226]/10">
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Day name</label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className={`${inputCls} w-48 font-serif text-2xl`} data-testid={`admin-day-label-${day.id}`} />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Date ({day.weekday})</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} w-44`} data-testid={`admin-day-date-${day.id}`} />
        </div>
        <div className="space-y-1 flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Intro line</label>
          <Input value={blurb} onChange={(e) => setBlurb(e.target.value)} className={inputCls} />
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button data-testid={`admin-day-save-${day.id}`} onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-[#0E6B3A] text-[#FBF7F0] px-4 py-2 text-sm hover:bg-[#083D21]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save day
            </button>
          )}
          <div className="flex items-center gap-1" title="Change the order days appear on the page">
            <button
              data-testid={`admin-day-up-${day.id}`}
              onClick={() => onMove(day.id, -1)}
              disabled={isFirst}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#8E1226]/20 text-[#8E1226] hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Move ${day.label} earlier`}
            >
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              data-testid={`admin-day-down-${day.id}`}
              onClick={() => onMove(day.id, 1)}
              disabled={isLast}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#8E1226]/20 text-[#8E1226] hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Move ${day.label} later`}
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
          <ConfirmDelete
            testId={`admin-day-delete-${day.id}`}
            label="Delete day"
            description="Deletes this entire day, all its roles, time slots and sign-ups."
            onConfirm={async () => {
              try {
                await client.delete(`/admin/days/${day.id}`);
                toast.success("Day deleted.");
                reload();
              } catch (e) {
                toast.error("Could not delete day.");
              }
            }}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {day.roles.map((r) => (
          <RoleBlock key={r.id} role={r} client={client} reload={reload} />
        ))}
      </div>
      <AddRole dayId={day.id} client={client} reload={reload} />
    </div>
  );
}

export default function ShiftsPanel({ token }) {
  const client = authApi(token);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDay, setNewDay] = useState({ label: "", date: "", blurb: "" });
  const [addingDay, setAddingDay] = useState(false);

  const reload = async () => {
    try {
      const { data } = await client.get("/admin/shifts");
      setDays(data.days);
    } catch (e) {
      toast.error("Could not load shifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addDay = async () => {
    if (!newDay.label.trim() || !newDay.date) {
      toast.error("Enter a name and date for the new day.");
      return;
    }
    setAddingDay(true);
    try {
      await client.post("/admin/days", newDay);
      setNewDay({ label: "", date: "", blurb: "" });
      toast.success("Day added.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAddingDay(false);
    }
  };

  const moveDay = async (dayId, dir) => {
    const ids = days.map((d) => d.id);
    const i = ids.indexOf(dayId);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await client.post("/admin/days/reorder", { day_ids: ids });
      toast.success("Day order updated — the page now leads with the first day.");
      reload();
    } catch {
      toast.error("Could not reorder days.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#6D726A] py-10">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading shifts…
      </div>
    );
  }

  return (
    <div data-testid="admin-shifts-panel">
      {days.map((d, idx) => (
        <DayBlock
          key={d.id}
          day={d}
          client={client}
          reload={reload}
          onMove={moveDay}
          isFirst={idx === 0}
          isLast={idx === days.length - 1}
        />
      ))}

      <div className="rounded-2xl border border-dashed border-[#8E1226]/25 p-6 md:p-8">
        <h3 className="font-serif text-2xl text-[#8E1226] mb-4">Add a new day</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Day name</label>
            <Input value={newDay.label} onChange={(e) => setNewDay({ ...newDay, label: e.target.value })} placeholder="e.g. St Stephen's Day" className={`${inputCls} w-56`} data-testid="admin-newday-label" />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Date</label>
            <Input type="date" value={newDay.date} onChange={(e) => setNewDay({ ...newDay, date: e.target.value })} className={`${inputCls} w-44`} data-testid="admin-newday-date" />
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-[0.18em] text-[#6D726A]">Intro line</label>
            <Input value={newDay.blurb} onChange={(e) => setNewDay({ ...newDay, blurb: e.target.value })} className={inputCls} />
          </div>
          <button data-testid="admin-newday-add" onClick={addDay} disabled={addingDay} className="inline-flex items-center gap-2 rounded-full bg-[#8E1226] text-[#FBF7F0] px-6 py-3 text-sm hover:bg-[#0E6B3A] transition-colors">
            {addingDay ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add day
          </button>
        </div>
      </div>
    </div>
  );
}
